/**
 * Calculate optimal position for a context menu to ensure it stays within viewport
 * @param clickX - X coordinate of click event
 * @param clickY - Y coordinate of click event
 * @param menuWidth - Width of the menu
 * @param menuHeight - Height of the menu
 * @returns Object with adjusted x and y coordinates
 */
export const calculateMenuPosition = (
  clickX: number,
  clickY: number,
  menuWidth: number = 200,
  menuHeight: number = 300
): { x: number; y: number } => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  
  // Default position (below and to the right of click)
  let x = clickX;
  let y = clickY;
  
  // Check if menu would overflow right edge
  if (x + menuWidth > viewportWidth) {
    x = viewportWidth - menuWidth - 10; // 10px margin from edge
  }
  
  // Check if menu would overflow left edge (after adjusting for right overflow)
  if (x < 10) {
    x = 10; // 10px margin from left edge
  }
  
  // Check if menu would overflow bottom edge
  if (y + menuHeight > viewportHeight) {
    // Try to position above the click
    y = clickY - menuHeight - 10;
    
    // If still overflowing top edge, position at bottom with scroll
    if (y < 10) {
      y = viewportHeight - menuHeight - 10;
    }
  }
  
  // Ensure minimum margin from top
  if (y < 10) {
    y = 10;
  }
  
  return { x, y };
};

/**
 * Calculate position for tooltip to ensure it stays within viewport
 * @param elementRect - Bounding rectangle of the element
 * @param tooltipWidth - Width of the tooltip
 * @param tooltipHeight - Height of the tooltip
 * @returns Object with x and y coordinates for tooltip
 */
export const calculateTooltipPosition = (
  elementRect: DOMRect,
  tooltipWidth: number = 200,
  tooltipHeight: number = 120
): { x: number; y: number } => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  
  // Position above the element by default
  let x = elementRect.left + elementRect.width / 2;
  let y = elementRect.top - 10;
  
  // Center horizontally
  x -= tooltipWidth / 2;
  
  // Check if tooltip would overflow left edge
  if (x < 10) {
    x = 10;
  }
  
  // Check if tooltip would overflow right edge
  if (x + tooltipWidth > viewportWidth) {
    x = viewportWidth - tooltipWidth - 10;
  }
  
  // Check if tooltip would overflow top edge (when positioned above)
  if (y - tooltipHeight < 10) {
    // Position below the element instead
    y = elementRect.bottom + 10;
    
    // Check if tooltip would overflow bottom edge when positioned below
    if (y + tooltipHeight > viewportHeight) {
      y = viewportHeight - tooltipHeight - 10;
    }
  }
  
  return { x, y };
};